import i18n from "@i18n";

import getContext, { FetchInAppServerContextError } from "./context";
import { getEmailAndName } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";

import store from "./redux/store";
import { updateRecommendedPlan } from "./redux/slices/recommendedPlan";
import { updatePlans } from "./redux/slices/plans";
import { updateServerContext } from "./redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
} from "./redux/slices/userData";
import { updateTrialFromServerContext } from "./redux/slices/trial";
import { setLanguage } from "./redux/slices/language";
import { setCountry } from "./redux/slices/countryCode";

import ConversionTracker from "./services/ConversionTracker";
import EventTracker from "./services/EventTracker";
import UserData from "./services/UserData";
import SentryHelper from "./services/SentryHelper";
import OptimizelyExperiment from "./services/OptimizelySXExperiment";
import ExperimentTracker from "./services/ExperimentTracker";
import VisitTracker from "./services/VisitTracker";

export default async function initializeApplication() {
  try {
    // First get the serverContext from cache or refetch.
    const serverContext = await getServerContextOnInitialization();
    // Second update the UserData slice in redux (also, fetch userData if an existing user).
    await updateUserDataSlice(serverContext);
    // Third rehydrate redux state with serverContext.
    await updateStoreWithServerContext(serverContext);
    // Fourth initialize the various services we use to sync data.

    initializeGlobalServices(serverContext, USER_IS_BOT);
  } catch (e) {
    if (e instanceof FetchInAppServerContextError) {
      window.location = `/identification-error.html${window.location.search}`;
    } else {
      window.location = `/error.html${window.location.search}`;
    }

    // Throw another error to stop the rest of the JS from running after the redirect
    console.error(e);
    throw new Error("Failed to initialize App");
  }

  // Initialize tracking utilizing the embedded window.meristemContext.
  initializeAndTrackMeristemV2Experiment();
}

/**
 * Initialize global services and variables for use within our components.
 */
function initializeGlobalServices(
  serverContext,
  // NOTE(patrick): We had issues with bots with duplicated cookies or application
  // state getting lumped into one experiment variation which would mess up
  // experiment reporting. We use a bot detection library and make sure they get
  // a minimal experience
  userIsBot = false
) {
  // Set up Sentry and set the user.
  window.SentryHelper = new SentryHelper(serverContext.user_id);
  window.serverContext = serverContext;
  window.trialFeeWaived = serverContext.trial_fee_waived;

  // Update i18n language to match serverContext language.
  if (i18n && serverContext.language_code !== i18n.language) {
    i18n.changeLanguage(serverContext.language_code);
  }

  if ("plans" in serverContext && 4 in serverContext.plans) {
    window.trialFee = serverContext.plans[4].trial_fee;
  } else if ("plan" in serverContext) {
    window.trialFee = serverContext.plan.trial_fee;
  }
  window.hasTrialFee = !window.trialFeeWaived && window.trialFee > 0;

  window.experimentTracker = new ExperimentTracker();
  window.visitTracker = new VisitTracker(serverContext.session_cookie_domain);

  // Set up the event trackers and make them available globally.
  window.tracker = new EventTracker(userIsBot);

  // Set up the userData stub
  window.userData = new UserData();

  // Set up conversion tracker for pixel tracking.
  window.conversionTracker = new ConversionTracker();

  window.osxe = new OptimizelyExperiment();

  document.querySelector("html").lang = serverContext.language_code;
}

/**
 * Get serverContext on initialization from cache with retry if fetching from cache fails.
 */
async function getServerContextOnInitialization() {
  try {
    return await window.serverContextLoaded;
  } catch (err) {
    console.error(err);
    window.serverContextLoaded = getContext();
    return window.serverContextLoaded;
  }
}

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 * @param {*} serverContext
 */
async function updateUserDataSlice(serverContext) {
  // Fetch user from backend if they are in the database.
  // Guard against bots, don't attempt to fetch userData if we detect a bot.
  if (serverContext.user_in_db !== false && !USER_IS_BOT) {
    await store.dispatch(
      fetchAndInitializeExistingUser(serverContext.user_id, serverContext)
    );
  } else {
    await store.dispatch(initializeNewUser(serverContext));
  }
  const emailAndName = getEmailAndName();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}

/**
 * After we fetch serverContext, Take the data and update the appropriate
 * redux slices with it.
 * @param {*} serverContext
 */
export async function updateStoreWithServerContext(serverContext) {
  await store.dispatch(updateServerContext(serverContext));
  await store.dispatch(setLanguage(serverContext.language_code));
  await store.dispatch(setCountry(serverContext.country_code));
  await store.dispatch(updateTrialFromServerContext(serverContext));

  if (serverContext?.plans)
    await store.dispatch(updatePlans(serverContext.plans));
  if (serverContext?.plan)
    await store.dispatch(updateRecommendedPlan(serverContext.plan));
}

/**
 * Initialize the Visit Tracker with Experiment tags and track OnMeristemExperimentDecided
 */
function initializeAndTrackMeristemV2Experiment() {
  const experimentsState = window?.meristemContext?.experimentsCookie || [];
  window.visitTracker.init(
    ExperimentTracker.formatMeristemExperimentStateVisitData(
      experimentsState
    ) || {}
  );

  experimentsState.forEach(experimentState => {
    window.tracker.trackGenericEvent("OnMeristemExperimentDecided", {
      experimentName: experimentState.experimentName || null,
      variationName: experimentState.variationName || null,
    });
  });
}
